/* eslint-disable camelcase */
import { AnalyticsStore, ContextStore } from 'bernie-plugin-mobx';
import { inject, observer } from 'mobx-react';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import * as React from 'react';

import { AdvertisingStore } from 'bernie-plugin-ads-loader-script';
import { mergeQueryString, updateSearch } from 'bernie-core';
import { QueryObject } from 'bernie-core/dist/routing/update-search';

// Components
import { Experiment, ExperimentControl, ExperimentVariant } from '@shared-ui/experiment-context';
import { AmenitiesAdaptExProvider } from '@shared-ui/lodging-property-details';
import { ContactHostContextProvider } from '@shared-ui/retail-lodging-contact-host';
import { CustomerNotificationLocation } from '@shared-ui/customer-notifications';
import { PubSubEventManager } from '@shared-ui/signal-events-context_pub-sub-observer';
import { Qualtrics } from '@shared-ui/customer-qualtrics';
import { RefProviderRoot } from '@shared-ui/ref-context';
import { SignalEventsContextProvider } from '@shared-ui/signal-events-context';

import { CurrentHotelStore } from 'stores/current-hotel-store';
import { MultiItemStore } from 'stores/multi-item-store';
import { RefProvider } from 'src/common/stores/contexts/ref-provider';
import { UIPageStore } from 'stores/ui-page-store';

import { InfositePageName } from 'constants/pages';

import { ApolloLink, useApolloClient } from '@apollo/client';
import { CustomerNotifications } from 'components/common/customer-notifications';
import { ErrorLink } from 'components/infosite/error-link';
import { LegalDisclaimer } from 'components/infosite/template-components/legal-disclaimer';
import { MainRegion } from 'components/layout/pages/main-region';
import { MasterPage, MasterPageRoute } from 'components/layout/pages/master-page';
import { MesoFooter } from 'components/meso/meso-footer';
import { parse } from 'query-string';
import { ShortlistProvider } from 'components/common/shortlist/shortlist-provider';
import { TemplateRenderer } from 'components/infosite/template-components/template-renderer';
import { useInfositePageTitleCacheRead } from 'components/infosite/use-infosite-page-title-cache-read';
import { useMultiItemCacheRead } from 'src/common/components/infosite/use-multi-item-context-cache-read';

interface InfositeProps extends RouteComponentProps {
  advertising?: AdvertisingStore;
  analytics?: AnalyticsStore;
  context?: ContextStore;
  currentHotel?: CurrentHotelStore;
  multiItem?: MultiItemStore;
  route?: MasterPageRoute;
  uiPage?: UIPageStore;
}

interface InfositeState {
  roomsAndRatesTarget?: any;
  searchFormRef?: any;
}

const Infosite = inject(
  'currentHotel',
  'analytics',
  'multiItem',
  'page'
)(({ currentHotel, analytics, multiItem, page, ...props }) => {
  const client = useApolloClient();
  useInfositePageTitleCacheRead(currentHotel, page);
  useMultiItemCacheRead(currentHotel, multiItem);

  const analyticsLink = new ApolloLink((operation, forward) =>
    forward(operation).map(response => {
      currentHotel.addPayload(operation.operationName, response?.extensions?.analytics[0], analytics);

      return response;
    })
  );

  if (typeof window !== 'undefined' && currentHotel && !currentHotel.apolloLinkSetOnBrowser) {
    client.setLink(ApolloLink.from([analyticsLink, client.link]));
    currentHotel.apolloLinkSetOnBrowser = true;
  } else if (currentHotel && !currentHotel.apolloLinkSetOnServer) {
    client.setLink(ApolloLink.from([analyticsLink, client.link]));
    currentHotel.apolloLinkSetOnServer = true;
  }

  return <InfositeClassComponent {...props} />;
});
export default Infosite;

@withRouter
@inject('advertising', 'analytics', 'context', 'currentHotel', 'multiItem', 'uiPage')
@observer
class InfositeClassComponent extends React.Component<InfositeProps, InfositeState> {
  constructor(props: InfositeProps) {
    super(props);

    if (
      this.props.context &&
      this.props.multiItem &&
      this.props.multiItem.isMixMatchEnabled &&
      typeof this.props.multiItem.getOrCreateCart === 'function' &&
      !this.props.multiItem.isPackageShoppingPath()
    ) {
      this.props.multiItem.getOrCreateCart(this.props.context);
    }
    this.props.advertising?.includeAd();
    if (this.props.multiItem?.multiItemContext) {
      this.props.advertising?.trigger(InfositePageName[this.props.multiItem.multiItemContext.packageType]);
    } else {
      this.props.advertising?.trigger(InfositePageName.SA);
    }
  }

  componentDidMount() {
    const { location, history } = this.props;
    const { propertyUnitRoomInfoDialog } = parse(location.search);

    if (propertyUnitRoomInfoDialog) {
      const query = mergeQueryString(location.search, {}, key => !key.startsWith('propertyUnitRoomInfoDialog'));
      const queryCombiner = (prevQuery: QueryObject, newQuery: QueryObject) => newQuery;
      updateSearch({ history, location, newParams: query, replace: true, queryCombiner });
    }

    window.scrollTo(0, 0);
  }

  componentDidUpdate(prevProps: InfositeProps) {
    const { uiPage, location, history } = this.props;
    const { scrollPosition } = parse(location.search);
    if (scrollPosition) {
      uiPage?.setFocusItem(scrollPosition);
      const query = mergeQueryString(location.search, {}, key => !key.startsWith('scrollPosition'));
      const queryCombiner = (prevQuery: QueryObject, newQuery: QueryObject) => newQuery;
      updateSearch({ history, location, newParams: query, replace: true, queryCombiner });
    } else if (location && prevProps.location && location.pathname !== prevProps.location.pathname && history.action === 'PUSH') {
      window.scrollTo(0, 0);
    }
  }

  render() {
    const { currentHotel, route } = this.props;

    const { searchCriteria, isFailure } = currentHotel || {};

    const masterProps = {
      'data-stid': 'infosite-page-container',
      pageUsable: true,
      route,
      searchCriteria,
    };

    return (
      <RefProvider>
        <RefProviderRoot>
          <ShortlistProvider>
            <AmenitiesAdaptExProvider>
              <SignalEventsContextProvider eventManager={PubSubEventManager}>
                <MasterPage
                  {...masterProps}
                  templateContent={
                    <MainRegion>
                      {isFailure && <ErrorLink />}
                      <ContactHostContextProvider>
                        <Experiment name="increased_real_estate_shopping_experience">
                          <ExperimentControl>
                            <div className="constrain-width">
                              <TemplateRenderer />
                              <CustomerNotifications location={CustomerNotificationLocation.FOOTER} />
                            </div>
                          </ExperimentControl>
                          <ExperimentVariant bucket={1}>
                            <div className="increased_real_estate_shopping_experience_1">
                              <TemplateRenderer />
                              <CustomerNotifications location={CustomerNotificationLocation.FOOTER} />
                            </div>
                          </ExperimentVariant>
                          <ExperimentVariant bucket={2}>
                            <div className="increased_real_estate_shopping_experience_2">
                              <TemplateRenderer />
                              <CustomerNotifications location={CustomerNotificationLocation.FOOTER} />
                            </div>
                          </ExperimentVariant>
                        </Experiment>
                      </ContactHostContextProvider>
                      <MesoFooter />
                      <LegalDisclaimer />
                    </MainRegion>
                  }
                />
              </SignalEventsContextProvider>
            </AmenitiesAdaptExProvider>
          </ShortlistProvider>
          <Experiment name="Lodging_PWA_Qualtrics">
            <ExperimentControl />
            <ExperimentVariant bucket={1}>
              <Qualtrics />
            </ExperimentVariant>
          </Experiment>
        </RefProviderRoot>
      </RefProvider>
    );
  }
}
